import { useEffect, useState } from "react";
import { Loader2, Save, Trash2, UserPlus, ArrowUp, ArrowDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { AdminTitle } from "./ui";

function Input({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      {textarea ? <textarea rows={5} className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        : <input className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
    </div>
  );
}

function TimelineEditor({ steps, onChange }) {
  const upd = (i, k, v) => onChange(steps.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const move = (i, d) => { const n = [...steps]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <section className="card space-y-4 p-6" data-testid="settings-timeline">
      <h2 className="font-display text-xl font-bold uppercase">Frise « Les étapes de sa vie »</h2>
      {steps.map((s, i) => (
        <div key={i} className="grid gap-3 rounded-lg border p-4 sm:grid-cols-[10rem_1fr_auto]">
          <Input label="Date" value={s.date} onChange={(v) => upd(i, "date", v)} testid={`timeline-edit-date-${i}`} />
          <Input label="Titre" value={s.title} onChange={(v) => upd(i, "title", v)} testid={`timeline-edit-title-${i}`} />
          <div className="flex items-end gap-1">
            <button type="button" className="btn-outline !px-2 !py-2" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Monter" data-testid={`timeline-edit-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
            <button type="button" className="btn-outline !px-2 !py-2" disabled={i === steps.length - 1} onClick={() => move(i, 1)} aria-label="Descendre" data-testid={`timeline-edit-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
            <button type="button" className="btn-outline !px-2 !py-2 text-asso" onClick={() => onChange(steps.filter((_, j) => j !== i))} aria-label="Supprimer" data-testid={`timeline-edit-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="sm:col-span-3"><Input label="Texte" textarea value={s.text} onChange={(v) => upd(i, "text", v)} testid={`timeline-edit-text-${i}`} /></div>
        </div>
      ))}
      <button type="button" className="btn-outline" onClick={() => onChange([...steps, { date: "[Date à compléter]", title: "", text: "" }])} data-testid="timeline-add-step">
        <Plus className="h-4 w-4" /> Ajouter une étape
      </button>
      <p className="text-xs text-neutral-500">Pensez à cliquer sur « Enregistrer » en haut de la page.</p>
    </section>
  );
}

export function SettingsAdmin() {
  const { settings, reload } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setS(JSON.parse(JSON.stringify(settings))); }, [settings]);
  if (!s) return <p>Chargement…</p>;
  const set = (path, val) => setS((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    const keys = path.split(".");
    keys.slice(0, -1).reduce((o, k) => o[k], next)[keys.at(-1)] = val;
    return next;
  });
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); await reload(); toast.success("Paramètres enregistrés"); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-4xl space-y-8" data-testid="admin-settings">
      <AdminTitle title="Paramètres & Ma Leba">
        <button className="btn-u !py-2" onClick={save} disabled={saving} data-testid="settings-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer</button>
      </AdminTitle>
      <section className="card space-y-4 p-6">
        <h2 className="font-display text-xl font-bold uppercase">Page Ma Leba</h2>
        <Input label="Phrase d'hommage (accueil)" value={s.maleba.hommage} onChange={(v) => set("maleba.hommage", v)} testid="settings-maleba-hommage" />
        <Input label="Qui était Ma Leba ?" textarea value={s.maleba.who_text} onChange={(v) => set("maleba.who_text", v)} testid="settings-maleba-who" />
        <Input label="Son histoire" textarea value={s.maleba.story_text} onChange={(v) => set("maleba.story_text", v)} testid="settings-maleba-story" />
        <Input label="Citation (facultatif, ne rien inventer)" value={s.maleba.quote} onChange={(v) => set("maleba.quote", v)} testid="settings-maleba-quote" />
      </section>
      <TimelineEditor steps={s.maleba.timeline || []} onChange={(v) => set("maleba.timeline", v)} />
      {["ong", "association"].map((k) => (
        <section key={k} className="card grid gap-4 p-6 sm:grid-cols-2">
          <h2 className="font-display text-xl font-bold uppercase sm:col-span-2">Coordonnées {k === "ong" ? "ONG" : "Association"}</h2>
          <Input label="E-mail" value={s.contacts[k].email} onChange={(v) => set(`contacts.${k}.email`, v)} testid={`settings-${k}-email`} />
          <Input label="Adresse" value={s.contacts[k].address} onChange={(v) => set(`contacts.${k}.address`, v)} testid={`settings-${k}-address`} />
          <div className="sm:col-span-2"><Input label="Téléphones (séparés par des virgules, format international)" value={s.contacts[k].phones.join(", ")}
            onChange={(v) => set(`contacts.${k}.phones`, v.split(",").map((x) => x.trim()).filter(Boolean))} testid={`settings-${k}-phones`} /></div>
        </section>
      ))}
      <section className="card grid gap-4 p-6 sm:grid-cols-2">
        <h2 className="font-display text-xl font-bold uppercase sm:col-span-2">WhatsApp, Présidente & réseaux sociaux</h2>
        <Input label="Numéro WhatsApp du widget" value={s.whatsapp} onChange={(v) => set("whatsapp", v)} testid="settings-whatsapp" />
        <Input label="Présidente" value={s.president} onChange={(v) => set("president", v)} testid="settings-president" />
        {Object.keys(s.socials).map((k) => (
          <Input key={k} label={`Lien ${k}`} value={s.socials[k]} onChange={(v) => set(`socials.${k}`, v)} testid={`settings-social-${k}`} />
        ))}
      </section>
    </div>
  );
}

export function UsersAdmin() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ email: "", name: "", role: "editor_ong", password: "" });
  const [pw, setPw] = useState({ current: "", new: "" });
  const load = () => api.get("/admin/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", f); toast.success("Utilisateur créé"); setF({ email: "", name: "", role: "editor_ong", password: "" }); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { if (window.confirm("Supprimer cet utilisateur ?")) { await api.delete(`/admin/users/${id}`); load(); } };
  const changePw = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/password", pw); toast.success("Mot de passe modifié"); setPw({ current: "", new: "" }); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="max-w-4xl space-y-8" data-testid="admin-users">
      <AdminTitle title="Utilisateurs" />
      <div className="card divide-y">
        {rows.map((u) => (
          <div key={u.id} className="flex items-center justify-between p-3 text-sm" data-testid={`user-row-${u.id}`}>
            <span><strong>{u.name}</strong> · {u.email} · <span className="text-u">{u.role}</span></span>
            {u.id !== user.id && <button onClick={() => del(u.id)} className="text-asso" aria-label="Supprimer" data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
      <form onSubmit={create} className="card grid gap-4 p-6 sm:grid-cols-2" data-testid="user-create-form">
        <h2 className="font-display text-xl font-bold uppercase sm:col-span-2">Ajouter un utilisateur</h2>
        <input required type="email" placeholder="E-mail" className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-create-email" />
        <input required placeholder="Nom" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-create-name" />
        <select className="input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-create-role">
          <option value="editor_ong">Éditeur ONG</option><option value="editor_association">Éditeur Association</option><option value="super_admin">Super-admin</option>
        </select>
        <input required type="password" minLength={8} placeholder="Mot de passe (8 caractères min.)" className="input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-create-password" />
        <button className="btn-u sm:col-span-2 sm:w-fit" data-testid="user-create-submit"><UserPlus className="h-4 w-4" /> Créer</button>
      </form>
      <form onSubmit={changePw} className="card grid gap-4 p-6 sm:grid-cols-2" data-testid="password-change-form">
        <h2 className="font-display text-xl font-bold uppercase sm:col-span-2">Changer mon mot de passe</h2>
        <input required type="password" placeholder="Mot de passe actuel" className="input" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="password-current" />
        <input required type="password" minLength={8} placeholder="Nouveau mot de passe" className="input" value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} data-testid="password-new" />
        <button className="btn-u sm:col-span-2 sm:w-fit" data-testid="password-submit">Modifier</button>
      </form>
    </div>
  );
}
